<template>
  <div
    ref="frame"
    class="datapeople-frame"
    :class="[bgColor === white ? 'bg-bgDefault' : 'bg-bgCanvas']"
    :style="{ 'overflow-x': scrollX }"
  >
    <AppHeader v-if="!isChromeView" class="frame-appheader" />
    <AppSideNav v-if="showAppSideNav" class="frame-sidebar" />
    <div class="frame-view">
      <router-view
        v-slot="{ Component }"
        @track-page-load="$emit('track-page-load', $event)"
      >
        <component :is="Component" />
      </router-view>
    </div>
    <PersonaModal />
    <DocumentPreview />
    <AcknowledgementPreview />
    <EmployeeAcknowledgementPreview v-if="showEmployeeAcknowledgementPreview" />
    <EmployeeAcknowledgementCompareModal v-if="showEmployeeAcknowledgementCompareModal" />
    <JobProfilePreview />
    <FlsaReportProfilePreview v-if="authData.flsaEnabled" />
    <ApprovalsLibraryPreview v-if="authData.jobProfileEnabled" />
    <JobProfileCommentsSidebar v-if="authData.jobProfileEnabled" />
    <CreateProfileTemplateModal
      :show="show.createProfileTemplate && authData.jobProfileEnabled"
      @close="setModal({ field: 'createProfileTemplate', value: false })"
    />
  </div>
</template>

<script>
import AppHeader from '@/components/shared/AppHeader.vue';
import AppSideNav from '@/components/shared/sideNav/AppSideNav.vue';
import { throttle } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { useModals } from '@/stores/modals/modals';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppBase } from '@/stores/app/base';
import { useSharePage } from '@/stores/app/sharePage';
import { useAppRoute } from '@/stores/app/route';
import DocumentPreview from '@/components/document/preview/DocumentPreview.vue';
import JobProfilePreview from '@/components/jobProfiles/editor/JobProfilePreview.vue';
import JobProfileCommentsSidebar from '@/components/document/comments/JobProfileCommentsSidebar.vue';
import PersonaModal from '@/components/shared/PersonaModal.vue';
import CreateProfileTemplateModal from '@/components/shared/CreateProfileTemplateModal.vue';
import { useColors } from '@/composables/colors';
import AcknowledgementPreview from '@/components/jobProfiles/AcknowledgementPreview.vue';
import EmployeeAcknowledgementPreview
  from '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementPreview.vue';
import EmployeeAcknowledgementCompareModal
  from '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementCompareModal.vue';
import FlsaReportProfilePreview
  from '@/components/library/flsaSurveys/FlsaReportProfilePreview.vue';
import ApprovalsLibraryPreview
  from '@/components/library/approvals/ApprovalsLibraryPreview.vue';
import { useEmployeeAcknowledgementsStore } from '@/stores/jobProfiles/employeeAcknowledgements';

export default {
  name: 'DatapeopleFrame',
  components: {
    AcknowledgementPreview,
    AppHeader,
    AppSideNav,
    DocumentPreview,
    EmployeeAcknowledgementPreview,
    EmployeeAcknowledgementCompareModal,
    FlsaReportProfilePreview,
    ApprovalsLibraryPreview,
    JobProfileCommentsSidebar,
    JobProfilePreview,
    PersonaModal,
    CreateProfileTemplateModal,
  },
  emits: ['track-page-load'],
  setup() {
    const { white, bgCanvas } = useColors();
    return {
      white,
      bgCanvas,
    };
  },
  computed: {
    ...mapState(useAppBase, ['windowWidth']),
    ...mapState(useModals, ['show']),
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useEmployeeAcknowledgementsStore, ['compareModalOpen']),
    ...mapState(useSharePage, ['isNotSharePage']),
    ...mapState(useAppRoute, ['isChromeView']),
    showEmployeeAcknowledgementPreview() {
      return this.authData.jobProfileEnabled && !this.compareModalOpen;
    },
    showEmployeeAcknowledgementCompareModal() {
      return this.authData.jobProfileEnabled
        && this.$route.query?.type === 'employee';
    },
    createProfilePrefill() {
      const {
        'job-code': jobCode,
        title,
        'job-title': jobTitle,
        location,
      } = this.$route.query;

      const prefill = {};

      if (jobCode) prefill.jobCode = jobCode;
      if (title) prefill.title = title;
      if (jobTitle) prefill.title = prefill.title || jobTitle;
      if (location) prefill.location = location;

      return prefill;
    },
    scrollX() {
      return (this.windowWidth < 1288)
        && (this.$route.query.view !== 'chrome')
        ? 'auto'
        : 'hidden';
    },
    bgColor() {
      const whiteBackgroundPages = [
        'editor-view', 'editor-view-reload', 'home', 'job-profile-view', 'job-profile-hris-view',
        'flsa-form-create', 'flsa-form-view', 'approval-actions',
      ];

      return whiteBackgroundPages.includes(this.$route.name)
        ? this.white
        : this.bgCanvas;
    },
    showAppSideNav() {
      return !this.isChromeView && this.isNotSharePage;
    },
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler(query) {
        const isCreatingDraft = query['new-draft'] === 'true';
        if (isCreatingDraft) {
          this.setModal({ field: 'createDraft', value: true });
          this.$router.replace({
            name: this.$route.name,
            query: { ...query, 'new-draft': undefined },
          });
        }

        const isCreatingEmail = query['new-email'] === 'true';
        if (isCreatingEmail) {
          this.setModal({ field: 'createEmail', value: true });
          this.$router.replace({
            name: this.$route.name,
            query: { ...query, 'new-email': undefined },
          });
        }

        const isCreatingProfile = query['new-profile'] === 'true';
        if (isCreatingProfile) {
          if (this.authData.jobProfileEnabled) {
            this.setModal({ field: 'createProfile', value: true });
          }
          this.$router.replace({
            name: this.$route.name,
            query: { ...query, 'new-profile': undefined },
          });
        }
      },
    },
    $route: {
      immediate: true,
      handler(newRoute, oldRoute) {
        // do not scroll when staying on the same page
        if (newRoute?.path === oldRoute?.path) return;

        this.$refs?.frame?.scrollTo(0, 0);
      },
    },
  },
  mounted() {
    this.$refs?.frame?.addEventListener('scroll', this.onScroll);
  },
  beforeUnmount() {
    this.$refs?.frame?.removeEventListener('scroll', this.onScroll);
  },
  errorCaptured(err) {
    // TODO: Investigate why this is necessary to capture errors
    console.error(err); // eslint-disable-line no-console

    if (this.view === 'chrome') {
      return true;
    }
    if (err.response && err.response.message === 'Login Required') {
      this.$router.push({ name: 'login' });
      return false;
    }
    return true;
  },
  methods: {
    ...mapActions(useModals, ['setModal']),
    ...mapActions(useAppBase, ['setScrollFrame']),
    onScroll(event) {
      this.onScrollThrottled(event);
    },
    onScrollThrottled: throttle(function onScrollThrottledCallback(event) {
      const { scrollTop = 0 } = this.$refs?.frame || {};
      this.setScrollFrame({ scrollTop, event });
    }, 200),
  },
};
</script>

<style scoped lang="less">
.datapeople-frame {
  display: grid;
  grid-template-areas:
    "header header"
    "sidebar view";
  grid-template-columns: auto minmax(auto, 1fr);
  grid-template-rows: auto 1fr;
  width: 100vw;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: v-bind(bgColor);
}

.frame-appheader {
  grid-area: header;
}

.frame-sidebar {
  grid-area: sidebar;
}

.frame-view {
  grid-area: view;
  overflow-x: auto;
}
</style>
